import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom'
import { useAuthStore } from '@/stores/auth-store'
import { AuthProvider } from '@/providers/auth-provider'
import { ThemeProvider } from '@/providers/theme-provider'
import { Shell } from '@/components/layout/shell'
import { ErrorBoundary } from '@/components/ui/error-boundary'
import LoginPage from '@/pages/login'
import WorkspacesPage from '@/pages/workspaces/index'
import NewWorkspacePage from '@/pages/workspaces/new'
import WorkspaceDetailPage from '@/pages/workspace/detail'
import WorkspaceMembersPage from '@/pages/workspace/members'
import WorkspaceSettingsPage from '@/pages/workspace/settings'
import WorkspacePipelinePage from '@/pages/workspace/pipeline'
import WorkspaceProjectsPage from '@/pages/workspace/projects/index'
import NewProjectPage from '@/pages/workspace/projects/new'
import ProjectDetailPage from '@/pages/workspace/projects/detail'
import NewTaskPage from '@/pages/workspace/tasks/new'
import TaskDetailPage from '@/pages/workspace/tasks/detail'
import DashboardPage from '@/pages/dashboard'
import ProfilePage from '@/pages/profile'

function RequireAuth({ children }: { children: React.ReactNode }) {
  const user = useAuthStore((s) => s.user)

  if (!user) {
    return <Navigate to="/login" replace />
  }

  return <>{children}</>
}

function wrap(element: React.ReactNode) {
  return <ErrorBoundary>{element}</ErrorBoundary>
}

export default function App() {
  return (
    <BrowserRouter>
      <ThemeProvider>
      <AuthProvider>
        <Routes>
          <Route path="/login" element={<LoginPage />} />
          <Route
            path="/"
            element={
              <RequireAuth>
                <Shell />
              </RequireAuth>
            }
          >
            <Route index element={wrap(<DashboardPage />)} />
            <Route path="profile" element={wrap(<ProfilePage />)} />
            <Route path="workspaces" element={wrap(<WorkspacesPage />)} />
            <Route path="workspaces/new" element={wrap(<NewWorkspacePage />)} />
            <Route path="workspaces/:slug" element={wrap(<WorkspaceDetailPage />)} />
            <Route path="workspaces/:slug/members" element={wrap(<WorkspaceMembersPage />)} />
            <Route path="workspaces/:slug/pipeline" element={wrap(<WorkspacePipelinePage />)} />
            <Route path="workspaces/:slug/settings" element={wrap(<WorkspaceSettingsPage />)} />
            <Route path="workspaces/:slug/projects" element={wrap(<WorkspaceProjectsPage />)} />
            <Route path="workspaces/:slug/projects/new" element={wrap(<NewProjectPage />)} />
            <Route path="workspaces/:slug/projects/:projectId" element={wrap(<ProjectDetailPage />)} />
            <Route path="workspaces/:slug/projects/:projectId/tasks/new" element={wrap(<NewTaskPage />)} />
            <Route path="workspaces/:slug/projects/:projectId/tasks/:taskId" element={wrap(<TaskDetailPage />)} />
          </Route>
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </AuthProvider>
      </ThemeProvider>
    </BrowserRouter>
  )
}
